import {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useTranslation,
  useSelector,
  useDispatch,
  lazy,
  Suspense,
} from '@spcc/core'
import {
  Button,
  Badge,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  PaginationControls,
  DateRangePicker,
  cn,
} from '@spcc/uikit'
import { RefreshCw, AlertTriangle, Activity } from '@spcc/uikit'

import { sapSyncActions } from '../../../store'
import {
  selectSyncJobs,
  selectDlqEntries,
  selectSapSyncLoading,
  selectSapSyncFilter,
  selectSelectedJob,
  selectCircuitBreakerStatus,
  selectDlqDepth,
  selectPullWatermarks,
  selectSyncJobStats,
  selectSyncJobsPagination,
} from '../../../store'
import type { SapSyncJob } from '../../../domain/types'
import { computeTotalJobs, buildDirectionOptions, buildEntityTypeOptions, buildOperationOptions, buildStatusOptions, buildDatePresets } from '../../utils/build-options'
import { CircuitBreakerCard } from '../CircuitBreakerCard'
import { SyncJobsTable, type SortField } from '../SyncJobsTable'
import { DlqTable } from '../DlqTable'
import { WatermarksTab } from './watermarks-tab'

const SyncJobDetailDrawer = lazy(() => import('../../drawers/sync-job-detail-drawer').then(m => ({ default: m.SyncJobDetailDrawer })))

const PAGE_SIZES = [10, 25, 50, 100]

function parseEntityIdsInput(value: string): string[] {
  const tokens = value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)

  return Array.from(new Set(tokens))
}

function buildEntityIdsInputValue(entityIds: string[] | undefined): string {
  if (!entityIds || entityIds.length === 0) {
    return ''
  }

  return entityIds.join(', ')
}

export function SapSyncSection() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const loading = useSelector(selectSapSyncLoading)
  const filter = useSelector(selectSapSyncFilter)
  const pagination = useSelector(selectSyncJobsPagination)
  const syncJobs = useSelector(selectSyncJobs)
  const dlqEntries = useSelector(selectDlqEntries)
  const selectedJob = useSelector(selectSelectedJob)
  const circuitBreaker = useSelector(selectCircuitBreakerStatus)
  const dlqDepth = useSelector(selectDlqDepth)
  const watermarks = useSelector(selectPullWatermarks)
  const syncJobStats = useSelector(selectSyncJobStats)

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [entityIdsInput, setEntityIdsInput] = useState<string>(buildEntityIdsInputValue(filter.entityIds))
  const sortField = (filter.sortField || 'createdAt') as SortField
  const sortOrder = filter.sortOrder || 'desc'

  const totalJobs = useMemo(() => computeTotalJobs(syncJobStats), [syncJobStats])

  const directionOptions = useMemo(() => buildDirectionOptions(t), [t])
  const entityTypeOptions = useMemo(() => buildEntityTypeOptions(t), [t])
  const operationOptions = useMemo(() => buildOperationOptions(t), [t])
  const statusOptions = useMemo(() => buildStatusOptions(t), [t])
  const datePresets = useMemo(() => buildDatePresets(t), [t])

  const dateRange = useMemo(() => ({
    from: filter.createdDateFrom ? new Date(filter.createdDateFrom) : undefined,
    to: filter.createdDateTo ? new Date(filter.createdDateTo) : undefined,
  }), [filter.createdDateFrom, filter.createdDateTo])

  // Fetch data on mount
  useEffect(() => {
    dispatch(sapSyncActions.fetchDashboardRequest())
    dispatch(sapSyncActions.fetchDlqRequest())
  }, [dispatch])

  // Fetch sync jobs when filter changes
  useEffect(() => {
    dispatch(sapSyncActions.fetchSyncJobsRequest(filter))
  }, [dispatch, filter])

  useEffect(() => {
    setEntityIdsInput(buildEntityIdsInputValue(filter.entityIds))
  }, [filter.entityIds])

  const handleRefresh = useCallback(() => {
    dispatch(sapSyncActions.fetchDashboardRequest())
    dispatch(sapSyncActions.fetchSyncJobsRequest(filter))
    dispatch(sapSyncActions.fetchDlqRequest())
  }, [dispatch, filter])

  const handleDirectionChange = useCallback(
    (value: string) => {
      const direction = value === 'all' ? undefined : (value as typeof filter.direction)
      dispatch(sapSyncActions.setFilter({ direction, page: 1 }))
    },
    [dispatch],
  )

  const handleEntityTypeChange = useCallback(
    (value: string) => {
      const entityType = value === 'all' ? undefined : (value as typeof filter.entityType)
      dispatch(sapSyncActions.setFilter({ entityType, page: 1 }))
    },
    [dispatch],
  )

  const handleOperationChange = useCallback(
    (value: string) => {
      const sapOperation = value === 'all' ? undefined : (value as typeof filter.sapOperation)
      dispatch(sapSyncActions.setFilter({ sapOperation, page: 1 }))
    },
    [dispatch],
  )

  const handleStatusChange = useCallback(
    (value: string) => {
      const status = value === 'all' ? undefined : (value as typeof filter.status)
      dispatch(sapSyncActions.setFilter({ status, page: 1 }))
    },
    [dispatch],
  )

  const handleEntityIdsInputChange = useCallback(
    (value: string) => {
      setEntityIdsInput(value)
    },
    [],
  )

  const handleApplyEntityIdsFilter = useCallback(
    () => {
      const entityIds = parseEntityIdsInput(entityIdsInput)
      dispatch(
        sapSyncActions.setFilter({
          entityIds: entityIds.length > 0 ? entityIds : undefined,
          page: 1,
        }),
      )
    },
    [dispatch, entityIdsInput],
  )

  const handleEntityIdsInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return
      }

      event.preventDefault()
      handleApplyEntityIdsFilter()
    },
    [handleApplyEntityIdsFilter],
  )

  const handleDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined } | undefined) => {
      if (range?.from || range?.to) {
        dispatch(sapSyncActions.setFilter({
          createdDateFrom: range.from ? range.from.toISOString().split('T')[0] : undefined,
          createdDateTo: range.to ? range.to.toISOString().split('T')[0] : undefined,
          page: 1,
        }))
      } else {
        dispatch(sapSyncActions.setFilter({
          createdDateFrom: undefined,
          createdDateTo: undefined,
          page: 1,
        }))
      }
    },
    [dispatch],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(sapSyncActions.setPage(page))
    },
    [dispatch],
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(sapSyncActions.setPageSize(pageSize))
    },
    [dispatch],
  )

  const handleColumnSort = useCallback(
    (field: SortField) => {
      const newOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'
      dispatch(sapSyncActions.setSort({ sortField: field, sortOrder: newOrder }))
    },
    [dispatch, sortField, sortOrder],
  )

  const handleRowClick = useCallback(
    (job: SapSyncJob) => {
      setSelectedRowId(String(job.id))
      dispatch(sapSyncActions.selectJob(job))
    },
    [dispatch],
  )

  const handleCloseDrawer = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatch(sapSyncActions.selectJob(null))
        setSelectedRowId(null)
      }
    },
    [dispatch],
  )

  const handleRetryDlq = useCallback(
    (id: number) => {
      dispatch(sapSyncActions.retryDlqRequest(id))
    },
    [dispatch],
  )

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('sapSync.sapIntegration', 'SAP Integration')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t(
              'sapSync.sapIntegrationDescription',
              'Monitor SAP sync jobs, circuit breaker status, and dead letter queue',
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 me-2', loading && 'animate-spin')} />
          {t('sapSync.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CircuitBreakerCard status={circuitBreaker} />

        <Card className="border border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              {t('sapSync.dlqDepth', 'DLQ Depth')}
            </div>
            <p
              className={cn(
                'mt-2 text-2xl font-bold',
                dlqDepth > 0 ? 'text-orange-600' : 'text-foreground',
              )}
            >
              {dlqDepth}
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Activity className="h-4 w-4" />
              {t('sapSync.totalJobs', 'Total Jobs')}
            </div>
            <p className="mt-2 text-2xl font-bold">{totalJobs}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue="sync-jobs" className="w-full">
        <TabsList>
          <TabsTrigger value="sync-jobs">
            {t('sapSync.syncJobsTab', 'Sync Jobs')}
          </TabsTrigger>
          <TabsTrigger value="dlq">
            {t('sapSync.dlqTab', 'Dead Letter Queue')}
            {dlqDepth > 0 && (
              <Badge variant="destructive" className="ms-2 h-5 min-w-5 px-1 text-xs">
                {dlqDepth}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="watermarks">
            {t('sapSync.pullWatermarks', 'Pull Watermarks')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sync-jobs" className="mt-4 space-y-4">
          {/* Filter toolbar */}
          <Card className="border border-border/50">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                <Select
                  value={filter.direction ?? 'all'}
                  onValueChange={handleDirectionChange}
                >
                  <SelectTrigger className="w-full sm:w-[160px] bg-background/50 border-border/50">
                    <SelectValue placeholder={t('sapSync.allDirections', 'All Directions')} />
                  </SelectTrigger>
                  <SelectContent>
                    {directionOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filter.entityType ?? 'all'}
                  onValueChange={handleEntityTypeChange}
                >
                  <SelectTrigger className="w-full sm:w-[180px] bg-background/50 border-border/50">
                    <SelectValue placeholder={t('sapSync.allEntityTypes', 'All Entity Types')} />
                  </SelectTrigger>
                  <SelectContent>
                    {entityTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filter.sapOperation ?? 'all'}
                  onValueChange={handleOperationChange}
                >
                  <SelectTrigger className="w-full sm:w-[200px] bg-background/50 border-border/50">
                    <SelectValue placeholder={t('sapSync.allOperations', 'All Operations')} />
                  </SelectTrigger>
                  <SelectContent>
                    {operationOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filter.status ?? 'all'}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-full sm:w-[150px] bg-background/50 border-border/50">
                    <SelectValue placeholder={t('sapSync.allStatuses', 'All Statuses')} />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  value={entityIdsInput}
                  onChange={(event) => handleEntityIdsInputChange(event.target.value)}
                  onKeyDown={handleEntityIdsInputKeyDown}
                  placeholder={t('sapSync.searchEntityId', 'Entity IDs (space or comma separated)')}
                  className="w-full sm:w-[300px] bg-background/50 border-border/50"
                />

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleApplyEntityIdsFilter}
                  className="w-full sm:w-auto"
                >
                  {t('sapSync.fetch', 'Fetch')}
                </Button>

                <DateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                  presets={datePresets}
                  placeholder={t('sapSync.selectDateRange', 'Select date range')}
                  className="w-full sm:w-auto sm:min-w-[200px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Sync Jobs Table */}
          {syncJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-foreground">
                {t('sapSync.noSyncJobs', 'No sync jobs found')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('sapSync.noSyncJobsDescription', 'Try adjusting your filter criteria')}
              </p>
            </div>
          ) : (
            <SyncJobsTable
              jobs={syncJobs}
              selectedRowId={selectedRowId}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleColumnSort}
              onRowClick={handleRowClick}
            />
          )}

          {/* Pagination */}
          {syncJobs.length > 0 && (
            <PaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.total}
              totalPages={pagination.pageCount}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizes={PAGE_SIZES}
            />
          )}
        </TabsContent>

        <TabsContent value="dlq" className="mt-4 space-y-4">
          {dlqEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-foreground">
                {t('sapSync.noDlqEntries', 'No dead letter queue entries')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('sapSync.noDlqEntriesDescription', 'All messages have been processed successfully')}
              </p>
            </div>
          ) : (
            <DlqTable
              entries={dlqEntries}
              onRetry={handleRetryDlq}
              retrying={loading}
            />
          )}
        </TabsContent>

        <TabsContent value="watermarks" className="mt-4">
          <WatermarksTab watermarks={watermarks} />
        </TabsContent>
      </Tabs>

      {/* Detail Drawer */}
      <Suspense fallback={null}>
        <SyncJobDetailDrawer
          job={selectedJob}
          open={selectedJob !== null}
          onOpenChange={handleCloseDrawer}
        />
      </Suspense>
    </div>
  )
}
